import {DataTable} from "../DataTable/DataTable";
import {CustomFieldDefEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useCustomFieldDefDataTableFilter} from "../CustomFieldDefDataTable/useCustomFieldDefDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useCustomFieldDefAction} from "../../menu/CustomFieldDefActionMenu/useCustomFieldDefAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {getEntityTypeDisplayValueAsString} from "../../../util/entity-ui-util";
import {YesNoText} from "../../text/YesNoText/YesNoText";
import { FormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: CustomFieldDefEntity[] = []

export type CustomFieldDefDataTableProps = EntityDataTableProps<CustomFieldDefEntity>

export const CustomFieldDefDataTable = (props: CustomFieldDefDataTableProps) => {
  const {
    records: defs = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useCustomFieldDefDataTableFilter({
    records: defs
  })

  const sortConfig = useMemo(() => {
    return {
      "label": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.label?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'label', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onCustomFieldDefActionClick
  } = useCustomFieldDefAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldDefEntity) => {
    e.stopPropagation()
    navigate(`/custom-field-defs/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldDefEntity) => {
    e.stopPropagation()
    onCustomFieldDefActionClick(record, 'EDIT')
  }, [onCustomFieldDefActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldDefEntity) => {
    e.stopPropagation()
    onCustomFieldDefActionClick(record, 'DELETE')
  }, [onCustomFieldDefActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<CustomFieldDefEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "label"),
        title: t("common.entity.customfielddef.table.column.label", "Label"),
        accessor: 'label',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "code"),
        title: t("common.entity.customfielddef.table.column.code", "Code"),
        accessor: 'code',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.customfielddef.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "forObjectType"),
        title: t("common.entity.customfielddef.table.column.forobject", "Object Type"),
        accessor: 'forObjectType',
        width: 100,
        sortable: true,
        render: ({forObjectType}) => {
          return getEntityTypeDisplayValueAsString(forObjectType, t)
        }
      },
      {
        ...getColumnFilterProps(filterConfig, "dataType"),
        title: t("common.entity.customfielddef.table.column.datatype", "Data Type"),
        accessor: 'dataType',
        width: 100,
        sortable: true,
        render: ({dataType}) => {
          return <FormattedDatabaseCode value={dataType}/>
        },
      },
      {
        title: t("common.entity.customfielddef.table.column.required", "Required"),
        accessor: 'required',
        width: 100,
        sortable: true,
        render: ({required}) => {
          return <YesNoText value={required}/>
        }
      },
      {
        title: '',
        accessor: "actions",
        width: 50,
        textAlignment: 'right',
        render: (def) => (
          <EntityActionGroup
            permissionPrefix="customfielddef"
            view={false}
            onViewActionClick={(e) => onViewActionClick(e, def)}
            onEditActionClick={(e) => onEditActionClick(e, def)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, def)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
